import { Box, Icon } from '@hover/blueprint';
import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from '@hover/blueprint/chakra';
import { iX } from '@hover/icons';
import { Component as ImportedWhiteHoverSVG } from '@hover/tokens-brand/assets/logo-mark--white.svg';
import { useActor } from '@xstate/react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { animated, useSpring } from 'react-spring';

import { RenderingCTATrackingIDs } from 'src/modules/designStudio/components/DesignMenu/RenderingRequest/types';
import { DesignStudioServiceContext } from 'src/modules/designStudio/providers';
import { Button } from 'src/shared/components';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import type { User } from 'src/shared/graphql/generated';
import { useProfileQuery } from 'src/shared/graphql/generated';
import {
  useIsLandscape,
  useReactNativeWebView,
  useTranslate,
} from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { moduleViewed } from 'src/shared/telemetry';

import { RenderingPopoverContent } from './RenderingPopoverContent';
import { RenderingPopoverHeader } from './RenderingPopoverHeader';
import {
  getButtonBounceAnimationConfig,
  getButtonSlideAnimation,
  getDefaultAnimationConfig,
} from './animationConfig';
import {
  useRenderingRequestCTAContent,
  useShouldAutoOpen,
  useShouldShowRenderingRequest,
} from './hooks';
import { openExistingRenderings, startNewRendering } from './services';

interface RenderingRequestModule {
  hasClosedDesignMenu: boolean;
  isDesignMenuOpen: boolean;
  closeDesignMenu: () => void;
  jobId?: number;
}

const RenderingRequest = ({
  hasClosedDesignMenu,
  jobId,
  isDesignMenuOpen = false,
  closeDesignMenu = () => null,
}: RenderingRequestModule) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [isLandscape] = useIsLandscape();
  const [isWebView, sendReactNativeMessage] = useReactNativeWebView();
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const { data: profileData } = useProfileQuery({ errorPolicy: 'all' });
  const currentUser = profileData?.profile?.user as User;

  const config = useRenderingRequestCTAContent(currentUser);

  const { appService } = useContext(DesignStudioServiceContext);
  const [state] = useActor(appService);

  const { orgId, userId, partnerId, hasRenderings } = state.context;
  const trackingProperties = useMemo(
    () => ({
      feature: FeatureTracking.RENDERING,
      user_id: userId,
      org_id: orgId,
      partner_id: partnerId,
    }),
    [userId, orgId, partnerId],
  );

  const [animatedProps, api] = useSpring(
    getDefaultAnimationConfig(isLandscape),
    [isLandscape],
  );
  const noRenderingsText: string = config.buttonText;
  const viewRenderingsText: string = t(
    'renderingPopover.ctaButtonText.viewRenderings',
  );
  const ctaButtonText = hasRenderings ? viewRenderingsText : noRenderingsText;

  const openPopover = useCallback(
    (delay?: number) => {
      closeDesignMenu();
      setHasBeenOpened(true);
      setTimeout(() => {
        setIsOpen(true);
      }, delay);
    },
    [closeDesignMenu],
  );

  const closePopover = () => {
    setIsOpen(false);
  };

  const triggerButtonBounce = (delay?: number) =>
    setTimeout(() => {
      api.start(getButtonBounceAnimationConfig(isLandscape));
    }, delay);

  useShouldAutoOpen({
    hasBeenOpened,
    hasClosedDesignMenu,
    openPopover,
    triggerButtonBounce,
  });

  // Track Module Viewed
  useEffect(() => {
    if (isOpen) {
      moduleViewed({
        job_id: jobId,
        ...trackingProperties,
        track_id: RenderingCTATrackingIDs.RENDERING_MODULE_CTA,
      });
    }
  }, [isOpen, jobId, trackingProperties]);

  useEffect(() => {
    api.start(getButtonSlideAnimation(isLandscape, isDesignMenuOpen));
  }, [isDesignMenuOpen, isLandscape, api]);

  return (
    <Popover
      closeOnBlur
      isOpen={isOpen}
      onClose={closePopover}
      onOpen={openPopover}
      placement="top-start"
    >
      <PopoverTrigger>
        <animated.div style={{ ...animatedProps, position: 'absolute' }}>
          <Button
            label="rendering_request_button"
            optionalTrackingProperties={{
              ...trackingProperties,
              track_id: RenderingCTATrackingIDs.MODULE_BUTTON,
            }}
            shape="circle"
            size="large"
          >
            {isOpen ? (
              <Icon
                height="80%"
                icon={iX}
                testId="rendering request popover close icon"
                width="80%"
              />
            ) : (
              <ImportedWhiteHoverSVG height="70%" width="70%" />
            )}
          </Button>
        </animated.div>
      </PopoverTrigger>
      <Box>
        <PopoverContent
          backgroundColor="neutral.backgroundDark"
          border="none"
          paddingX="19px"
          paddingY={400}
          width="auto"
        >
          <PopoverHeader>
            <RenderingPopoverHeader
              trackingProperties={{
                ...trackingProperties,
                track_id: RenderingCTATrackingIDs.VISIT_WEBSITE,
              }}
              visitWebsiteText={t('renderingPopover.visitWebsite')}
            />
          </PopoverHeader>
          <PopoverBody>
            <RenderingPopoverContent
              after={t('renderingPopover.after')}
              before={t('renderingPopover.before')}
              ctaButtonText={ctaButtonText}
              detailsText={config.blurb || t('renderingPopover.details')}
              handleRenderingRequestClick={() =>
                hasRenderings
                  ? openExistingRenderings({
                      isWebView,
                      sendReactNativeMessage,
                      jobId,
                    })
                  : startNewRendering({
                      isWebView,
                      jobId,
                      setIsLoading,
                      sendReactNativeMessage,
                    })
              }
              headingText={config.header || t('renderingPopover.heading')}
              learnMoreText={t('renderingPopover.learnMore')}
              loading={isLoading}
              trackingProperties={trackingProperties}
            />
          </PopoverBody>
        </PopoverContent>
      </Box>
    </Popover>
  );
};

export { RenderingRequest, useShouldShowRenderingRequest };
